/**
 * Shared data helpers for the Neusix Sales Console.
 *
 * Everything here reads what the existing internal API already returns. Values
 * the API does not provide are shown as "Not provided", never filled in.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDiscovery, fetchSessions } from '../api/client';
import { DISCOVERY_LAYERS } from '../config';
import { withoutSession } from './sessionDeletion';
import { CONSOLE_REFRESH_INTERVAL_MS, changedSessions, createPoller } from './polling';

// Who a discovery is for, and where its link points. Kept in their own
// module so the rules stay testable without React or the API client.
export { clientOf, clientUrl, companyOf, matchesSearch } from './identity';

export const NOT_PROVIDED = 'Not provided';
export const REPORT_NOT_AVAILABLE = 'Report not available';
export const TOTAL_STAGES = DISCOVERY_LAYERS.length;

/** Session lifecycle values the backend reports, in console wording. */
export const LIFECYCLE = {
  created: { label: 'Link issued', tone: 'neutral' },
  active: { label: 'In progress', tone: 'info' },
  completed: { label: 'Completed', tone: 'ok' },
  report_generated: { label: 'Report ready', tone: 'accent' },
  incomplete: { label: 'Incomplete', tone: 'warn' },
  interrupted: { label: 'Interrupted', tone: 'warn' },
  failed: { label: 'Failed', tone: 'danger' },
};

export function lifecycleLabel(lifecycle) {
  return LIFECYCLE[lifecycle]?.label || lifecycle || 'Unknown';
}

/** "L5 · Pain / Problem Discovery" for a stage code. */
export function stageLabel(code) {
  if (!code) return null;
  const layer = DISCOVERY_LAYERS.find((item) => item.id === code);
  return layer ? `${layer.id} · ${layer.label}` : code;
}

/**
 * Where a Business Discovery has got to, from its discovery status.
 *
 * `fallbackLevel` is the session's own `current_layer`, used when the
 * discovery status could not be read.
 */
export function progressOf(discovery, fallbackLevel) {
  if (!discovery) {
    return { label: stageLabel(fallbackLevel) || NOT_PROVIDED, done: null, complete: false, closed: false };
  }
  const done = (discovery.completed_levels || []).length;
  const complete = Boolean(discovery.closed) && done >= TOTAL_STAGES;
  let label;
  if (complete) label = 'Complete';
  else if (discovery.closed) label = `Ended at ${stageLabel(discovery.current_level)}`;
  else label = stageLabel(discovery.current_level);
  return { label, done, complete, closed: Boolean(discovery.closed) };
}

export function formatDateTime(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatDuration(seconds) {
  if (!seconds) return null;
  if (seconds < 60) return `${Math.round(seconds)} s`;
  return `${Math.round(seconds / 60)} min`;
}

export function greeting(now = new Date()) {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** A message fit for the console, never a raw network error. */
export function friendlyError(cause) {
  if (cause?.status) return cause.message || 'The request could not be completed.';
  return 'The Sales Console could not reach the server. Check that the backend is running, then try again.';
}

/**
 * Every session, newest first, plus each one's discovery status.
 *
 * The session list carries no company name, so the discovery status of the
 * first `detailLimit` sessions is read from the existing per-session endpoint,
 * a few at a time. `discoveries[id]` is `undefined` while loading and `null`
 * when it could not be read.
 *
 * The list is re-read every CONSOLE_REFRESH_INTERVAL_MS while the page is
 * mounted (see `polling.js`), so new sessions and names appear on their own.
 * A session whose list entry changed has its discovery status re-read too;
 * the old one stays on screen until the new one arrives. A failed refresh
 * keeps what is shown and says so; the next one clears it.
 */
export function useDiscoverySessions({ detailLimit = Infinity } = {}) {
  const [sessions, setSessions] = useState([]);
  const [discoveries, setDiscoveries] = useState({});
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const loadedRef = useRef({});
  const signaturesRef = useRef({});
  // Deleted here, so a refresh already in flight cannot bring them back.
  const deletedRef = useRef(new Set());
  const mountedRef = useRef(false);
  const pollerRef = useRef(null);
  const listRef = useRef(null);
  const inFlightRef = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const body = await fetchSessions();
      if (!mountedRef.current) return;
      const list = [...(body.sessions || [])]
        .filter((session) => !deletedRef.current.has(session.session_id))
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      const { changed, signatures } = changedSessions(signaturesRef.current, list);
      signaturesRef.current = signatures;
      for (const id of changed) delete loadedRef.current[id];
      // An unchanged list is not set again: nothing re-renders, nothing is
      // re-read, and the operator's view is left exactly as it is.
      const serialised = JSON.stringify(list);
      if (serialised !== listRef.current) {
        listRef.current = serialised;
        setSessions(list);
      }
      setError(null);
      setStatus('ready');
    } catch (cause) {
      if (!mountedRef.current) return;
      setError(friendlyError(cause));
      setStatus((current) => (current === 'ready' ? 'ready' : 'error'));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const poller = createPoller(load, { intervalMs: CONSOLE_REFRESH_INTERVAL_MS });
    pollerRef.current = poller;
    poller.start();
    return () => {
      mountedRef.current = false;
      poller.stop();
      pollerRef.current = null;
    };
  }, [load]);

  // Manual refresh ("Try again", a new link): now, through the same loop.
  const reload = useCallback(() => pollerRef.current?.refresh(), []);

  useEffect(() => {
    let cancelled = false;
    const wanted = sessions
      .slice(0, detailLimit)
      .map((session) => session.session_id)
      .filter((id) => !(id in loadedRef.current) && !inFlightRef.current.has(id));
    let next = 0;

    const worker = async () => {
      while (!cancelled && next < wanted.length) {
        const id = wanted[next];
        next += 1;
        let value = null;
        inFlightRef.current.add(id);
        try {
          value = await fetchDiscovery(id);
        } catch {
          value = null;
        } finally {
          inFlightRef.current.delete(id);
        }
        // Kept even if a refresh replaced the list meanwhile — it is the
        // freshest reading there is. Dropped only once the page has gone, or
        // the session has been deleted.
        if (!mountedRef.current || deletedRef.current.has(id)) return;
        loadedRef.current[id] = value;
        setDiscoveries((previous) => ({ ...previous, [id]: value }));
      }
    };

    Promise.all(Array.from({ length: Math.min(4, wanted.length) }, worker));
    return () => { cancelled = true; };
  }, [sessions, detailLimit]);

  // After a deletion: drop the row now, without waiting for a reload.
  const removeSession = useCallback((sessionId) => {
    setSessions((previous) => withoutSession(previous, sessionId));
    setDiscoveries((previous) => {
      const { [sessionId]: _gone, ...rest } = previous;
      return rest;
    });
    delete loadedRef.current[sessionId];
    deletedRef.current.add(sessionId);
  }, []);

  return { sessions, discoveries, status, error, reload, removeSession };
}
